import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DataTable, PageHeader, FilterSelect, StatusBadge, ConfirmDialog, useList } from "@/components/shared";
import { api, notifyErr, downloadFile, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KIND = { customers: "Clienti", rentals: "Noleggi" };

export default function ImportHistoryPage() {
  const { state } = useAuth();
  return state.user.role === "superadmin" ? <ImportHistory /> : <Navigate to="/" replace />;
}

function ImportHistory() {
  const nav = useNavigate();
  const { can } = useAuth();
  const [sp] = useSearchParams();
  const [kind, setKind] = useState(sp.get("kind") || "");
  const [rows, reload] = useList("/import/history", kind ? { kind } : {});
  const [undo, setUndo] = useState(null);
  const doUndo = async () => {
    try { const r = await api.post(`/import/${undo.id}/undo`); toast.success(r.data.message); reload(); } catch (e) { notifyErr(e); }
  };
  return (
    <div>
      <button onClick={() => nav(-1)} className="text-sm text-zinc-400 flex items-center gap-1 hover:text-white mb-4" data-testid="back-btn"><ArrowLeft size={14} />Indietro</button>
      <PageHeader title="Cronologia importazioni" subtitle="Tutte le importazioni CSV eseguite. Annullando un'importazione vengono rimossi solo i record creati da quel file.">
        <FilterSelect value={kind} onChange={setKind} placeholder="Tipo" options={[["customers", "Clienti"], ["rentals", "Noleggi"]]} testid="filter-import-kind" />
      </PageHeader>
      <DataTable rows={rows} testid="import-history-table" empty="Nessuna importazione eseguita" columns={[
        { key: "committed_at", label: "Data", render: (r) => fmtDateTime(r.committed_at || r.created_at) },
        { key: "kind", label: "Tipo", render: (r) => KIND[r.kind] || r.kind },
        { key: "filename", label: "File", render: (r) => <span className="font-medium">{r.filename}</span> },
        { key: "created_by", label: "Operatore" },
        { key: "result", label: "Esito", render: (r) => (r.result ? <span className="text-xs text-zinc-300" data-testid={`import-history-result-${r.id}`}>
          {r.result.imported} importati · {r.result.duplicates} duplicati · {r.result.skipped} saltati · {r.result.errors} non validi</span> : "—") },
        { key: "status", label: "Stato", render: (r) => (r.status === "undone"
          ? <StatusBadge text={`Annullata il ${fmtDateTime(r.undone_at)}`} tone="zinc" testid={`import-history-status-${r.id}`} />
          : <StatusBadge text="Completata" tone="green" testid={`import-history-status-${r.id}`} />) }]}
        actions={(r) => (
          <div className="flex gap-1 justify-end">
            <Button size="sm" variant="ghost" onClick={() => downloadFile(`/import/${r.id}/rejected`, `righe_non_importate_${r.kind}.csv`)} title="Righe non importate" data-testid={`import-history-rejected-${r.id}`}><Download size={15} /></Button>
            {r.status === "committed" && (r.result?.imported > 0) && can("customers", "delete") &&
              <Button size="sm" variant="outline" onClick={() => setUndo(r)} data-testid={`import-history-undo-${r.id}`}><Undo2 size={15} className="mr-1" />Annulla</Button>}
          </div>
        )} />
      <ConfirmDialog open={!!undo} onOpenChange={(o) => !o && setUndo(null)} destructive confirmText="Annulla importazione" title="Annullare questa importazione?"
        description={undo ? `${undo.filename}: verranno rimossi solo i ${undo.result?.imported ?? 0} record creati da questa importazione. I dati preesistenti non saranno toccati e i clienti collegati ad altri dati verranno mantenuti.` : ""}
        onConfirm={doUndo} />
    </div>
  );
}
